import Dialog from '@components/atoms/dialog/dialog';
import FeedbackWrap from '@components/catalog/my-sizes/my-sizes/response-feedback/feedback-dialog';
import Button from '@components/atoms/button/button';
import Icon from '@components/atoms/icon/icon';
import ProgressBar from '@components/atoms/progress-bar/progress-bar';
import MySizesFooter from '@components/catalog/my-sizes/my-sizes/footer/footer';
import React, { useCallback, useMemo, useState } from 'react';
import { useContextSelector } from 'use-context-selector';
import { UserContext } from '@context/user.context';
import { MySizes as Sizes, SizeCatType, SizeObjType, MySizesStepType } from '@interfaces/models/mySizes';
import StepOne, { ImageCard, PersonalizationUniverse } from '@components/catalog/my-sizes/my-sizes/steps/stepOne';
import StepSize from '@components/catalog/my-sizes/my-sizes/steps/stepSize';
import { RespStatusType } from '@components/catalog/my-sizes/my-sizes/response-feedback/types';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import useIsWebview from '@hooks/use-is-webview';
import { useSizesStepValues, SizesStepValueCollection } from '@hooks/size-guide/use-sizes-step-values';
import { useUniverseManagement } from '@hooks/size-guide/use-universe-management';
import styles from './add-my-sizes-dialog.module.scss';

const getUniverseData = (
  allAvailableSteps: MySizesStepType[],
  universe: PersonalizationUniverse,
): MySizesStepType | null => {
  if (universe == null || allAvailableSteps[universe] === undefined) {
    return null;
  }
  const data = allAvailableSteps[universe];
  return data || null;
};
interface AddMySizesDialogProps {
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dialogOpen: boolean;
  allAvailableSteps: MySizesStepType[];
  actionRef: React.MutableRefObject<string>;
  newSizesRef: React.MutableRefObject<Sizes | null>;
  handleUpdateSizes: () => void;
  getSizeId: (currentStep: number, universe: number, allAvailableSteps: MySizesStepType[]) => string;
  dataFetching: boolean;
  respStatus: RespStatusType;
  handleMySizeChange: (currentSizes: Sizes) => Promise<Sizes>;
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
}

const AddMySizesDialog: React.FC<AddMySizesDialogProps> = ({
  dialogOpen,
  allAvailableSteps,
  actionRef,
  newSizesRef,
  handleUpdateSizes,
  getSizeId,
  dataFetching,
  respStatus,
  setDialogOpen,
  handleMySizeChange,
  setCurrentStep,
  currentStep,
}) => {
  const { t: tA11y } = useTranslation('a11y');
  const [wizardSteps, setWizardSteps] = useState<React.ReactElement[]>([]);
  const isWebview = useIsWebview();

  const sizes = useContextSelector(UserContext, (v) => v.userSizes);
  const setUserSizes = useContextSelector(UserContext, (v) => v.setUserSizes);

  const { stepValue, stepValueRef, updateStepValue, resetStepValues } = useSizesStepValues();
  const { curUniverse, curUniverseRef, setUniverse } = useUniverseManagement();

  const handleNext = useCallback(() => {
    setCurrentStep((prev) => prev + 1);
  }, [setCurrentStep]);

  const handlePrev = (): void => {
    setCurrentStep((prev) => prev - 1);
  };

  // update step value
  const handleStepValueChange = useCallback(
    (_val: string, item: SizeObjType, checked: boolean, sizeData: SizeCatType): void => {
      const currentValues = { ...stepValueRef.current };
      const categoryKey = sizeData.label;

      // Initialize category if it doesn't exist
      if (!currentValues[categoryKey]) {
        currentValues[categoryKey] = {
          id: sizeData.id,
          label: sizeData.label,
          sizes: [],
        };
      }

      // Create a new copy of the category data
      const updatedCategory = {
        ...currentValues[categoryKey],
        sizes: [...currentValues[categoryKey].sizes],
      };

      if (checked) {
        // Add item if not already present
        if (!updatedCategory.sizes.some((size) => size.id === item.id)) {
          updatedCategory.sizes.push(item);
        }
      } else {
        // Remove item
        updatedCategory.sizes = updatedCategory.sizes.filter((size) => size.id !== item.id);
      }

      const newValue = {
        ...currentValues,
        [categoryKey]: updatedCategory,
      };

      updateStepValue(newValue);
    },
    [updateStepValue, stepValueRef],
  );

  // Check if we're on the last step of the wizard
  const lastStep = useMemo(() => {
    if (!curUniverse) {
      return false;
    }
    const universe = curUniverse.universe;
    const universeData = getUniverseData(allAvailableSteps, universe);
    if (!universeData?.size_categories) {
      return false;
    }

    return currentStep === universeData.size_categories.length;
  }, [currentStep, allAvailableSteps, curUniverse?.universe]);

  // Check if current step has any selected sizes
  const hasChecked = useMemo(() => {
    if (!curUniverse) {
      return false;
    }
    const universe = curUniverse.universe;
    const universeData = getUniverseData(allAvailableSteps, universe);
    if (!universeData?.size_categories) {
      return false;
    }

    const currentCategory = universeData.size_categories[currentStep - 1] as SizeCatType | undefined;
    if (!currentCategory) {
      return false;
    }

    return Boolean(stepValue[currentCategory.label]?.sizes.length > 0);
  }, [stepValue, allAvailableSteps, currentStep, curUniverse?.universe]);

  // Handle final step submission
  const lastStepCallback = useCallback(
    async (currentStepValues: SizesStepValueCollection): Promise<void> => {
      try {
        const currentUniverse = curUniverseRef.current;
        const data: Sizes = {
          filter: sizes?.filter || '',
          size_categories: Object.values(currentStepValues),
          universe: {
            ids: currentUniverse?.ids || [],
            label: currentUniverse?.title || '',
          },
        };

        actionRef.current = 'update';
        const updatedData = await handleMySizeChange(data);

        if (updatedData) {
          newSizesRef.current = updatedData;
        }
      } catch (error) {
        // Error is handled by UserContext's editUserSizes function,
        // which manages the error state and updates respStatus via userSizesError.
        // No need to re-throw error here.
      }
    },
    [sizes?.filter, handleMySizeChange, actionRef, newSizesRef, curUniverseRef],
  );

  // Generate MySizes steps based on selected universe
  const generateWizardSteps = useCallback(
    (selectedUniverse: PersonalizationUniverse): React.ReactElement[] => {
      const universeData = getUniverseData(allAvailableSteps, selectedUniverse) as MySizesStepType | null;
      if (!universeData?.size_categories) {
        return [];
      }

      const categories = universeData.size_categories as SizeCatType[];
      return categories.map((category: SizeCatType) => (
        <StepSize
          changeHandler={handleStepValueChange}
          key={`${category.id}-${selectedUniverse}`}
          sizeData={category}
        />
      ));
    },
    [allAvailableSteps, handleStepValueChange],
  );

  const handleUniverseSelection = useCallback(
    (selectedUniverse: ImageCard) => {
      setUniverse(selectedUniverse);
      resetStepValues();
      setWizardSteps(generateWizardSteps(selectedUniverse.universe));
      handleNext();
    },
    [setUniverse, resetStepValues, generateWizardSteps, handleNext],
  );

  const getActiveStep = useCallback(
    (modalDialogTitleId: string) => {
      const stepOne = (
        <StepOne
          key="pers-wiz-s1"
          onSelect={handleUniverseSelection}
          modalDialogTitleId={modalDialogTitleId}
        />
      );
      return [stepOne, ...wizardSteps][currentStep];
    },
    [currentStep, handleUniverseSelection, wizardSteps],
  );

  return (
    <Dialog
      isOpen={dialogOpen}
      closeDialog={() => {
        if (actionRef.current === 'update' && newSizesRef.current) {
          setUserSizes(newSizesRef.current);
        }
        setDialogOpen(false);
      }}
      styles={{
        content: {
          maxWidth: '755px',
          width: '100%',
          flexDirection: 'column',
          padding: '0px',
        },
      }}
      key={`${dialogOpen}`}
      hasCloseIcon={!isWebview}
      hasCloseTxt
      customClass={styles.creatingDialog}
      focusTrapActive={!isWebview}
      data-cy="add_my_sizes_dialog"
    >
      {(modalDialogTitleId) => (
        <FeedbackWrap
          loading={dataFetching}
          respStatus={respStatus}
          feedbackCallback={() => {
            handleUpdateSizes();
            setDialogOpen(false);
          }}
          modalDialogTitleId={modalDialogTitleId}
        >
          <>
            <div
              className={clsx(styles.add_my_sizes__wizard__backButtonAndPrograss, {
                [styles.add_my_sizes__wizard__backButtonAndPrograss__withCloseBtn]: !isWebview,
              })}
            >
              {!!currentStep && (
                <div className={styles.add_my_sizes__wizard__backButtonContainer}>
                  <Button onClick={() => handlePrev()}>
                    <Icon
                      name="arrow-head-left"
                      className={styles.add_my_sizes__wizard__backIcon}
                      ariaLabel={tA11y('A11Y.ICON.ARROW_PREVIOUS')}
                    />
                  </Button>
                </div>
              )}
              <div className={styles.add_my_sizes__wizard__progressBarContainer}>
                <ProgressBar value={(currentStep * 100) / wizardSteps.length} />
              </div>
            </div>
            <div className={styles.wizard}>{getActiveStep(modalDialogTitleId)}</div>

            {currentStep > 0 && (
              <MySizesFooter
                lastStep={lastStep}
                lastStepCallback={() => lastStepCallback(stepValue)}
                hasChecked={hasChecked}
                handleNext={handleNext}
                allAvailableSteps={allAvailableSteps}
                sizeId={getSizeId(currentStep, curUniverse.universe ?? 0, allAvailableSteps)}
                drawerCustomClass={styles.drawerStylefix}
                modalDialogTitleId={modalDialogTitleId}
              />
            )}
          </>
        </FeedbackWrap>
      )}
    </Dialog>
  );
};

export default AddMySizesDialog;
